import { Button, Card, Col, Divider, Flex, Form, FormProps, Input, Row, Segmented, Select, Skeleton, Tooltip, Typography } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultOptionType } from 'antd/es/select';
import { useNavigate } from 'react-router-dom';

// components
import FormElement from 'components/formElement/formElement';
import CompoundElement from 'components/compoundElement/compoundElement';
import StatusComponent from 'components/status/status';
import PageTitle from 'components/title/title';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';

// services

// styles

// models
import { SuspendTypeResponse } from '../models/suspend';
import NotificationService from 'core/services/notification';
import { onCheckCompanyShareholder, onGetSuspendRequestReasons, onSubmitSuspendRequest } from '../services/suspendServices';
import { isRTL } from 'core/services/language';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import VerificationModal from '../../verificationModal';
import { ServiceCost, VerificationPayload } from '../../commonModels';
import FormListFilesElement from 'components/documentsListForm/documentsListForm';
import { checkArabicValidation } from 'utils/validators/arabic';
import { checkEnglishValidation } from 'utils/validators/english';
import { formatErrorMessages } from 'utils/errorMessage';
import { onGetServiceCost } from '../../commonServices';
import ActionCostComponent from '../../cost';
import { useDispatch } from 'react-redux';
import { subtractPoints } from 'stores/useCompanyStore';
import InfoTooltip from 'components/info/info';

interface SuspendCreatePageProps {}

type FormFields = {
	type: string;
	suspend_types: string[];
	verified_using: 'password' | 'otp';
	password?: string;
	otp?: string;
	national_id: String;
	company_shareholder_id: string;
	suspended_common_shares_count?: string;
	suspend_all_common_shares?: string;
	files: any;
};

const SuspendCreatePage: FunctionComponent<SuspendCreatePageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [suspendForm] = Form.useForm();

	const commonShares = Form.useWatch('suspended_common_shares_count', suspendForm) || 0;
	const preferredShares = Form.useWatch('suspended_preferred_shares_count', suspendForm) || 0;

	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [generalServiceCost, setGeneralServiceCost] = useState<ServiceCost>();
	const [specificServiceCost, setSpecificServiceCost] = useState<ServiceCost>();

	const [isChecking, setIsChecking] = useState<boolean>(false);
	const [checked, setChecked] = useState<boolean>(false);
	const [shareholderShares, setShareholderShares] = useState<{ common: number; preferred: number }>({ common: 0, preferred: 0 });

	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isTypesLoading, setIsTypesLoading] = useState<boolean>(false);

	const [suspendTypeOptions, setSuspendTypeOptions] = useState([
		{
			value: 'General',
			label: t('ACTIONS.GENERAL'),
		},
		{
			value: 'Specific',
			label: t('ACTIONS.SPECIFIC'),
		},
	]);
	const [suspendType, setSuspendType] = useState<string>('General');
	const [suspendReasons, setSuspendReasons] = useState<SuspendTypeResponse>();
	const [suspendReasonsOptions, setSuspendReasonsOptions] = useState<DefaultOptionType[]>([]);
	const [selectedReason, setSelectedReason] = useState<string[]>([]);

	useEffect(() => {
		getSuspendTypes();
		getServiceCost();
	}, []);

	const validateArabic = (_: any, value: string) => {
		if (checkArabicValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ARABIC_ONLY')));
		else return Promise.reject();
	};

	const validateEnglish = (_: any, value: string) => {
		if (checkEnglishValidation(_, value)) {
			return Promise.resolve();
		} else if (value) return Promise.reject(new Error(t('COMMON.ENGLISH_ONLY')));
		else return Promise.reject();
	};

	const getSuspendTypes = async () => {
		try {
			setIsTypesLoading(true);

			const response = await onGetSuspendRequestReasons();
			setSuspendReasons(response);

			setSuspendReasonsOptions(
				response.general.map((item) => ({
					label: isRTL() ? item.translated_title.ar : item.translated_title.en,
					value: item.id,
				})),
			);

			setIsTypesLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/suspend');
		}
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const responses = await Promise.all([onGetServiceCost('specific_suspend'), onGetServiceCost('general_suspend')]);

			setSpecificServiceCost(responses[0]?.success);
			setGeneralServiceCost(responses[1]?.success);

			setSuspendTypeOptions([
				{
					value: 'General',
					label: t('ACTIONS.GENERAL'),
				},
				{
					value: 'Specific',
					label: t('ACTIONS.SPECIFIC'),
				},
			]);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const onChangeSuspendType = (value: string) => {
		setSuspendType(value);

		const options =
			value === 'General'
				? suspendReasons?.general.map((item) => ({
						label: isRTL() ? item.translated_title.ar : item.translated_title.en,
						value: item.id,
					}))
				: suspendReasons?.specific.map((item) => ({
						label: isRTL() ? item.translated_title.ar : item.translated_title.en,
						value: item.id,
					}));

		setSuspendReasonsOptions(options as DefaultOptionType[]);
		setSelectedReason([]);
	};

	const checkShareholder = async (nationalId: string) => {
		try {
			setIsChecking(true);

			const response = await onCheckCompanyShareholder(nationalId);

			suspendForm.setFieldsValue({
				national_id: response.company_shareholder.shareholder.national_id,
				company_shareholder_id: response.company_shareholder.id,
				shareholder_name: isRTL()
					? response.company_shareholder.shareholder.name
					: (response.company_shareholder.shareholder.name_en ?? response.company_shareholder.shareholder.name),
			});

			setShareholderShares({
				common: response.company_shareholder.free_common_shares_count,
				preferred: response.company_shareholder.free_preferred_shares_count,
			});

			setIsChecking(false);
			setChecked(true);
		} catch (error: any) {
			setIsChecking(false);

			NotificationService.error(t('COMMON.ERROR'), formatErrorMessages(error));
		}
	};

	const onFinish: FormProps<FormFields>['onFinish'] = (values: any) => {
		setVerificationModalOpen(true);
	};

	const onSubmit = async ({ key, method }: VerificationPayload) => {
		try {
			setIsSubmitting(true);
			const values = suspendForm.getFieldsValue();

			const payload = new FormData();

			payload.append('type', suspendType);
			payload.append('verified_using', method);
			payload.append(method, key);
			payload.append('description[ar]', values.description_ar);
			payload.append('description[en]', values.description_en);

			suspendForm.getFieldValue('files')?.forEach((fileObj: { file: File; title: string }, index: number) => {
				payload.append(`files[${index}][file]`, fileObj.file);
				payload.append(`files[${index}][title]`, fileObj.title);
			});

			selectedReason.forEach((reason, index) => {
				payload.append(`suspend_types[${index}]`, reason.toString());
			});

			if (suspendType == 'Specific') {
				payload.append('national_id', values.national_id);
				payload.append('company_shareholder_id', values.company_shareholder_id);
				payload.append('suspended_common_shares_count', values.suspended_common_shares_count || '0');
				payload.append('suspend_all_common_shares', '0');
			}

			await onSubmitSuspendRequest(payload);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.SUSPEND_CREATED_SUCCESSFULLY'));

			if (specificServiceCost?.payment_method === 'points') dispatch(subtractPoints(specificServiceCost.payment_details.points_required || 0));

			navigate('/dashboard/actions/suspend');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), formatErrorMessages(error));
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton />

			<Form form={suspendForm} onFinish={onFinish} initialValues={{ files: [{}] }}>
				<Card>
					<ComponentHeader title={t('ACTIONS.SUSPEND')} subtitle={t('ACTIONS.FILL_DETAILS_TO_VALIDATE_ORDER')} />

					<Segmented
						className="custom white"
						onChange={onChangeSuspendType}
						options={suspendTypeOptions}
						block
						size="large"
						style={{ marginBottom: 30 }}
					/>

					<Row gutter={[16, 16]}>
						<Col span={24}>
							<h5>{t(`ACTIONS.${suspendType.toUpperCase()}_SUSPENSION`)}</h5>
						</Col>

						{suspendType === 'Specific' && (
							<>
								<Col span={24} md={12}>
									<Form.Item name="national_id" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
										<CompoundElement
											buttonText={t('COMMON.CHECK')}
											onSubmit={checkShareholder}
											label={t('COMMON.NATIONAL_ID')}
											isLoading={isChecking}
											onChange={() => {
												suspendForm.setFieldsValue({ national_id: '', company_shareholder_id: '', shareholder_name: '' });
												setChecked(false);
											}}
										/>
									</Form.Item>
								</Col>

								{checked && (
									<>
										<Col span={24} md={12}>
											<FormElement label={t('ACTIONS.SHAREHOLDER_NAME')} name="shareholder_name" type="text" disabled />
										</Col>

										<Col span={24}>
											<Form.Item name="company_shareholder_id" hidden>
												<Input hidden />
											</Form.Item>
										</Col>

										{shareholderShares.common > 0 && (
											<Col span={24} md={12}>
												<FormNumberElement
													name="suspended_common_shares_count"
													label={t('ACTIONS.SUSPENDED_COMMON_SHARES_COUNT')}
													max={shareholderShares.common}
													rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
													addonAfter={
														<StatusComponent
															style={{ paddingBlock: 3 }}
															bordered
															color="green"
															small
															text={`${t('COMMON.AVAILABLE')}: ${shareholderShares.common - commonShares}`}
														/>
													}
												/>
											</Col>
										)}

										{shareholderShares.preferred > 0 && (
											<Col span={24} md={12}>
												<FormNumberElement
													name="suspended_preferred_shares_count"
													label={t('ACTIONS.SUSPENDED_PREFERRED_SHARES_COUNT')}
													max={shareholderShares.preferred}
													addonAfter={
														<StatusComponent
															style={{ paddingBlock: 3 }}
															bordered
															color="green"
															small
															text={`${t('COMMON.AVAILABLE')}: ${shareholderShares.preferred - preferredShares}`}
														/>
													}
												/>
											</Col>
										)}
									</>
								)}

								<Col span={24}>
									<Divider />
								</Col>
							</>
						)}
					</Row>

					<Row gutter={[16, 16]} className="mt-3">
						<Col span={24} md={12}>
							<Flex gap={0} align="center" style={{ marginBottom: 5 }}>
								<Typography style={{ marginBottom: 0, fontSize: 15 }}>{t('ACTIONS.SUSPEND_REASON')}</Typography>
								<InfoTooltip title={t('ACTIONS.SUSPEND_REASON_TOOLTIP')} />
							</Flex>
							<Form.Item name="suspendReason" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
								<Select
									mode="multiple"
									allowClear
									style={{ display: 'block', width: 'var(--inputWidth)', maxWidth: '100%' }}
									value={selectedReason}
									onChange={setSelectedReason}
									options={suspendReasonsOptions}
									loading={isTypesLoading}
								/>
							</Form.Item>
						</Col>

						<Col span={24} md={12}></Col>

						<Col span={24} md={12}>
							<FormElement
								label={t('COMMON.ARABIC_DESC')}
								name="description_ar"
								type="text"
								rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateArabic }]}
							/>
						</Col>

						<Col span={24} md={12}>
							<FormElement
								label={t('COMMON.ENGLISH_DESC')}
								name="description_en"
								type="text"
								rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }, { validator: validateEnglish }]}
							/>
						</Col>

						<Col span={24}>
							<Divider className="my-0" />
						</Col>

						<Col span={24}>
							<Flex gap={0} align="center" style={{ marginBottom: 5 }}>
								<h5 className="mb-0">{t('ACTIONS.UPLOAD_DOCUMENTS')}</h5>
								<InfoTooltip title={t('ACTIONS.UPLOAD_DOCUMENTS_TOOLTIP')} />
							</Flex>
						</Col>
					</Row>

					<FormListFilesElement form={suspendForm} fieldName="files" />

					<Flex justify="flex-end" align="center" gap={20} className="mt-5">
						{serviceCostLoading ? (
							<Skeleton.Button style={{ width: 150, height: 65, borderRadius: 'var(--radius)' }} />
						) : (
							<ActionCostComponent serviceCost={suspendType === 'Specific' ? specificServiceCost! : generalServiceCost!} />
						)}
						<Tooltip
							color="red"
							title={
								(suspendType === 'Specific' ? !specificServiceCost?.can_access : !generalServiceCost?.can_access)
									? t('ACTIONS.INSUFFICIENT_BALANCE')
									: ''
							}>
							<Button
								type="primary"
								htmlType="submit"
								loading={isSubmitting}
								disabled={suspendType === 'Specific' ? !specificServiceCost?.can_access : !generalServiceCost?.can_access}>
								{t('COMMON.SUBMIT')}
							</Button>
						</Tooltip>
					</Flex>
				</Card>
			</Form>

			<VerificationModal
				open={verificationModalOpen}
				isLoading={isSubmitting}
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={onSubmit}
				module="suspend"
			/>
		</>
	);
};

export default SuspendCreatePage;
